import React, { useEffect, useState, useMemo, useCallback } from 'react';
import {
  Building2, Save, Loader2, Plus, Trash2, Star, Edit2, X, Power, PowerOff,
  Eye, EyeOff, KeyRound, ShieldCheck, ShieldAlert, Zap, TestTube, Radio,
  Copy, Check, ExternalLink, Info,
} from 'lucide-react';
import { fetchWithStepUp } from './StepUpModal';
import LogoUpload from './ui-shared/LogoUpload';
import LogoPlacementEditor from './ui-shared/LogoPlacementEditor';
import InvoiceHtmlEditor from './InvoiceHtmlEditor';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const emptyEnv = () => ({ client_id: '', secret: '', webhook_id: '', enabled: true, secret_set: false, secret_last4: '' });

const blank = () => ({
  name: '', company_number: '', vat: '',
  address: '', city: '', country: '',
  phone: '', email: '', website: '',
  bank_details: '', logo_url: '', logo_data_url: '',
  invoice_settings: {},
  invoice_prefix: 'INV', invoice_due_days: 7,
  is_default: false,
  role: '', enabled: true, apply_tax: false, tax_rate: 0,
  paypal_mode: 'sandbox', paypal_enabled: true,
  paypal_sandbox: emptyEnv(),
  paypal_live:    emptyEnv(),
});

const ROLE_LABELS = {
  USA_COMPANY: { label: 'USA Company', tint: 'from-blue-500 to-indigo-600' },
  UK_COMPANY:  { label: 'UK Company',  tint: 'from-red-500 to-rose-600' },
  EU_COMPANY:  { label: 'EU Company',  tint: 'from-emerald-500 to-teal-600' },
};

export default function CompaniesEditor({ token }) {
  const [companies, setCompanies] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null);
  const [draft, setDraft] = useState(blank());
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState('');
  const [activeTab, setActiveTab] = useState('sandbox'); // which env tab is expanded in the modal
  const [showSecret, setShowSecret] = useState({ sandbox: false, live: false });
  const [testing, setTesting] = useState(null);      // 'sandbox' | 'live' | null
  const [testResult, setTestResult] = useState({ sandbox: null, live: null });

  const headers = useMemo(
    () => ({ Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' }),
    [token],
  );

  const load = useCallback(async () => {
    setLoading(true); setErr('');
    try {
      const r = await fetch(`${API_URL}/api/companies/`, { headers: { Authorization: `Bearer ${token}` } });
      if (!r.ok) throw new Error(`HTTP ${r.status}`);
      const list = await r.json();
      // Ensure nested PayPal objects exist
      setCompanies(list.map(c => ({
        ...c,
        paypal_sandbox: c.paypal_sandbox || emptyEnv(),
        paypal_live:    c.paypal_live    || emptyEnv(),
      })));
    } catch (e) { setErr(e.message); }
    finally { setLoading(false); }
  }, [token]);

  useEffect(() => { load(); }, [load]);

  const beginNew = () => {
    setEditing('new'); setDraft(blank()); setErr('');
    setActiveTab('sandbox'); setTestResult({ sandbox: null, live: null });
  };
  const beginEdit = (c) => {
    setEditing(c.id);
    setDraft({
      ...c,
      paypal_sandbox: { ...emptyEnv(), ...(c.paypal_sandbox || {}), secret: '' },
      paypal_live:    { ...emptyEnv(), ...(c.paypal_live    || {}), secret: '' },
    });
    setErr('');
    setActiveTab(c.paypal_mode || 'sandbox');
    setTestResult({ sandbox: null, live: null });
  };
  const cancelEdit = () => { setEditing(null); setDraft(blank()); setErr(''); };

  // Normalise the FastAPI 422/403 `detail` payload into a single
  // string we can safely drop into a React node. `detail` may be:
  //   • a string (plain message)
  //   • an object with `message` (our HTTPException convention)
  //   • a step-up challenge `{code, step_up_required, …}` — handled
  //     by `fetchWithStepUp` upstream, but if we end up here it means
  //     the user cancelled the TOTP modal
  //   • a Pydantic ValidationError array (`[{loc, msg, type}, …]`).
  // Anything else → JSON.stringify so the admin sees the raw payload
  // instead of crashing React with "objects are not valid as a child".
  const detailToString = (detail, fallback) => {
    if (!detail) return fallback;
    if (typeof detail === 'string') return detail;
    if (Array.isArray(detail)) {
      return detail
        .map((d) => (d?.msg ? `${(d.loc || []).slice(-1)[0] || 'field'}: ${d.msg}` : JSON.stringify(d)))
        .join('; ');
    }
    if (typeof detail === 'object') {
      if (detail.message) return detail.message;
      if (detail.detail) return detailToString(detail.detail, fallback);
      try { return JSON.stringify(detail); } catch { return fallback; }
    }
    return String(detail);
  };

  const save = async () => {
    setSaving(true); setErr('');
    try {
      const url = editing === 'new'
        ? `${API_URL}/api/companies/`
        : `${API_URL}/api/companies/${editing}`;
      const method = editing === 'new' ? 'POST' : 'PUT';
      // Flatten PayPal nested for backend (it accepts both forms but flat is safer)
      const body = {
        ...draft,
        paypal_sandbox_client_id:  draft.paypal_sandbox.client_id,
        paypal_sandbox_secret:     draft.paypal_sandbox.secret,   // empty = keep
        paypal_sandbox_webhook_id: draft.paypal_sandbox.webhook_id,
        paypal_sandbox_enabled:    !!draft.paypal_sandbox.enabled,
        paypal_live_client_id:     draft.paypal_live.client_id,
        paypal_live_secret:        draft.paypal_live.secret,
        paypal_live_webhook_id:    draft.paypal_live.webhook_id,
        paypal_live_enabled:       !!draft.paypal_live.enabled,
      };
      delete body.paypal_sandbox;
      delete body.paypal_live;
      // Switching paypal_mode from sandbox → live is gated behind
      // step-up MFA on the backend (purpose: `company_update` /
      // `company_create`). fetchWithStepUp pops the TOTP modal on a
      // 403 step-up challenge and re-runs the request transparently.
      const r = await fetchWithStepUp(url, {
        method, headers, body: JSON.stringify(body),
      });
      const res = await r.json().catch(() => ({}));
      if (!r.ok) {
        setErr(detailToString(res.detail, `HTTP ${r.status}`));
        return;
      }
      setEditing(null);
      load();
    } catch (e) {
      setErr(e?.message || 'Save failed');
    } finally { setSaving(false); }
  };

  const remove = async (c) => {
    if (!window.confirm(`Delete "${c.name}"? This cannot be undone.`)) return;
    try {
      // Deleting a billing entity is sensitive — go through step-up
      // so the modal pops on the 403 challenge instead of silently
      // doing nothing.
      const r = await fetchWithStepUp(`${API_URL}/api/companies/${c.id}`, {
        method: 'DELETE', headers: { Authorization: `Bearer ${token}` },
      });
      if (!r.ok) {
        const res = await r.json().catch(() => ({}));
        setErr(detailToString(res.detail, `Delete failed: HTTP ${r.status}`));
        return;
      }
      load();
    } catch (e) {
      setErr(e?.message || 'Delete failed');
    }
  };

  const setDefault = async (c) => {
    try {
      const r = await fetchWithStepUp(`${API_URL}/api/companies/${c.id}`, {
        method: 'PUT', headers, body: JSON.stringify({ is_default: true }),
      });
      if (!r.ok) {
        const res = await r.json().catch(() => ({}));
        setErr(detailToString(res.detail, `Set default failed: HTTP ${r.status}`));
        return;
      }
      load();
    } catch (e) {
      setErr(e?.message || 'Set default failed');
    }
  };

  // Quick enable/disable — same PATCH semantic used inside the Edit
  // dialog, but exposed as a one-click chip on the card so admin can
  // pause a company (e.g. suspend an EU billing entity during an
  // audit) without opening the full modal. Matches the pattern used
  // in the Priorities editor for consistency.
  const [toggleBusy, setToggleBusy] = useState(new Set());
  const toggleEnabled = async (c) => {
    const nextEnabled = !c.enabled;
    // Guard: don't allow disabling the default company — a disabled
    // default would leave the app without a billing entity to pick.
    if (!nextEnabled && c.is_default) {
      setErr('Cannot disable the default company. Make another company default first.');
      return;
    }
    if (!nextEnabled && !window.confirm(`Disable "${c.name}"? Existing orders / invoices keep the company; new orders will pick another enabled one.`)) return;
    setToggleBusy(prev => { const s = new Set(prev); s.add(c.id); return s; });
    try {
      const r = await fetchWithStepUp(`${API_URL}/api/companies/${c.id}`, {
        method: 'PUT', headers, body: JSON.stringify({ enabled: nextEnabled }),
      });
      if (!r.ok) {
        const res = await r.json().catch(() => ({}));
        setErr(detailToString(res.detail, `Toggle failed: HTTP ${r.status}`));
        return;
      }
      load();
    } catch (e) {
      setErr(e?.message || 'Toggle failed');
    } finally {
      setToggleBusy(prev => { const s = new Set(prev); s.delete(c.id); return s; });
    }
  };

  const testPaypal = async (mode) => {
    if (editing === 'new') {
      setTestResult(s => ({ ...s, [mode]: { success: false, message: 'Save the company first, then test.' } }));
      return;
    }
    setTesting(mode);
    setTestResult(s => ({ ...s, [mode]: null }));
    try {
      const env = draft[`paypal_${mode}`] || {};
      const r = await fetch(`${API_URL}/api/companies/${editing}/test-paypal`, {
        method: 'POST', headers,
        body: JSON.stringify({
          mode,
          client_id: env.client_id,
          secret:    env.secret, // empty = use stored
        }),
      });
      const j = await r.json().catch(() => ({}));
      setTestResult(s => ({ ...s, [mode]: { success: !!j.success, message: j.message || j.detail || `HTTP ${r.status}` } }));
    } catch (e) {
      setTestResult(s => ({ ...s, [mode]: { success: false, message: e.message } }));
    } finally {
      setTesting(null);
    }
  };

  const setEnvField = (mode, key, value) => {
    setDraft(d => ({ ...d, [`paypal_${mode}`]: { ...(d[`paypal_${mode}`] || emptyEnv()), [key]: value } }));
    setTestResult(s => ({ ...s, [mode]: null }));
  };

  if (loading) {
    return (
      <div className="bg-white rounded-xl p-12 text-center text-slate-500 border border-slate-200">
        <Loader2 className="w-5 h-5 inline animate-spin mr-2" /> Loading companies…
      </div>
    );
  }

  return (
    <div className="space-y-5" data-testid="companies-editor">
      <div className="flex items-start justify-between gap-3">
        <div className="flex items-start gap-3 p-4 bg-cyan-50 border border-cyan-200 rounded-xl flex-1">
          <Building2 className="w-5 h-5 text-cyan-700 mt-0.5" />
          <div className="text-sm text-cyan-900">
            <p className="font-semibold mb-1">My Companies</p>
            <p className="text-cyan-800">
              Billing entities with dual PayPal credentials (Sandbox for tests, Live for production).
              Switch mode per-company from the <strong>Active mode</strong> dropdown inside each edit dialog.
            </p>
          </div>
        </div>
        <button onClick={beginNew} data-testid="company-add"
          className="px-4 py-2 bg-gradient-to-r from-cyan-500 to-blue-600 text-white rounded-lg font-medium flex items-center gap-2 hover:shadow-md flex-shrink-0">
          <Plus className="w-4 h-4" /> Add company
        </button>
      </div>

      {err && <div className="p-3 bg-red-50 border border-red-200 rounded-lg text-sm text-red-700">{err}</div>}

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        {companies.map((c) => {
          const active = c.paypal_mode === 'live' ? c.paypal_live : c.paypal_sandbox;
          const activeReady = !!(active?.client_id && active?.secret_set);
          return (
            <div key={c.id}
              className={`bg-white rounded-xl border p-5 ${c.is_default ? 'border-cyan-300 ring-1 ring-cyan-200' : 'border-slate-200'}`}
              data-testid={`company-card-${c.id}`}>
              <div className="flex items-start justify-between gap-3 mb-3">
                <div className="flex-1 min-w-0">
                  <p className="text-base font-bold text-slate-800 truncate">{c.name}</p>
                  {c.company_number && <p className="text-xs text-slate-500">Company No. {c.company_number}</p>}
                  <div className="mt-1.5 flex flex-wrap items-center gap-1">
                    {c.role && ROLE_LABELS[c.role] && (
                      <span className={`px-2 py-0.5 rounded text-[10px] font-bold text-white bg-gradient-to-r ${ROLE_LABELS[c.role].tint}`}>
                        {ROLE_LABELS[c.role].label}
                      </span>
                    )}
                    {c.role && (c.enabled
                      ? <span className="px-2 py-0.5 rounded text-[10px] font-bold bg-emerald-100 text-emerald-700">ENABLED</span>
                      : <span className="px-2 py-0.5 rounded text-[10px] font-bold bg-red-100 text-red-700">DISABLED</span>
                    )}
                    {c.apply_tax && <span className="px-2 py-0.5 rounded text-[10px] font-bold bg-amber-100 text-amber-700">+{c.tax_rate}% TAX</span>}
                    {c.role && (
                      c.paypal_enabled && activeReady
                        ? <span className={`px-2 py-0.5 rounded text-[10px] font-bold flex items-center gap-1 ${c.paypal_mode === 'live' ? 'bg-green-100 text-green-700' : 'bg-blue-100 text-blue-700'}`} data-testid={`company-paypal-ready-${c.id}`}>
                            <ShieldCheck className="w-3 h-3" /> PAYPAL {c.paypal_mode.toUpperCase()}
                          </span>
                        : <span className="px-2 py-0.5 rounded text-[10px] font-bold bg-slate-100 text-slate-500 flex items-center gap-1" data-testid={`company-paypal-missing-${c.id}`}>
                            <ShieldAlert className="w-3 h-3" /> PAYPAL {c.paypal_mode.toUpperCase()} NOT CONFIGURED
                          </span>
                    )}
                    {/* Per-env badges */}
                    {c.paypal_sandbox?.client_id && (
                      <span className={`px-1.5 py-0.5 rounded text-[9px] font-semibold ${c.paypal_sandbox.enabled ? 'bg-slate-100 text-slate-600' : 'bg-slate-200 text-slate-400 line-through'}`}>
                        🧪 sandbox
                      </span>
                    )}
                    {c.paypal_live?.client_id && (
                      <span className={`px-1.5 py-0.5 rounded text-[9px] font-semibold ${c.paypal_live.enabled ? 'bg-slate-100 text-slate-600' : 'bg-slate-200 text-slate-400 line-through'}`}>
                        🟢 live
                      </span>
                    )}
                  </div>
                </div>
                {c.is_default && (
                  <span className="text-[10px] font-bold bg-cyan-100 text-cyan-700 px-2 py-0.5 rounded-full flex items-center gap-1">
                    <Star className="w-3 h-3" /> default
                  </span>
                )}
              </div>

              <div className="text-sm text-slate-600 space-y-0.5">
                {c.address && <p>{c.address}</p>}
                {(c.city || c.country) && <p>{[c.city, c.country].filter(Boolean).join(', ')}</p>}
                {c.vat && <p className="text-xs"><span className="text-slate-400">VAT:</span> {c.vat}</p>}
                {c.phone && <p className="text-xs"><span className="text-slate-400">Tel:</span> {c.phone}</p>}
                {c.email && <p className="text-xs"><span className="text-slate-400">Email:</span> {c.email}</p>}
              </div>

              <div className="flex flex-wrap gap-2 mt-4 pt-4 border-t border-slate-100">
                <button onClick={() => beginEdit(c)} data-testid={`company-edit-${c.id}`}
                  className="px-3 py-1.5 bg-slate-100 hover:bg-slate-200 rounded-lg text-xs font-semibold text-slate-700 flex items-center gap-1.5">
                  <Edit2 className="w-3 h-3" /> Edit
                </button>
                {!c.is_default && (
                  <button onClick={() => setDefault(c)} data-testid={`company-default-${c.id}`}
                    className="px-3 py-1.5 bg-cyan-50 hover:bg-cyan-100 text-cyan-700 rounded-lg text-xs font-semibold flex items-center gap-1.5">
                    <Star className="w-3 h-3" /> Make default
                  </button>
                )}
                {/* Quick enable/disable — pauses a company without opening
                    the Edit modal. Matches the one-click pattern used in
                    the Priorities editor. Default company can't be
                    disabled (guarded server-side + client-side). */}
                <button
                  onClick={() => toggleEnabled(c)}
                  disabled={toggleBusy.has(c.id) || (c.enabled && c.is_default)}
                  data-testid={`company-toggle-enabled-${c.id}`}
                  title={c.is_default ? 'Default company cannot be disabled — pick another default first' : (c.enabled ? 'Temporarily disable this company' : 'Re-enable this company')}
                  className={`px-3 py-1.5 rounded-lg text-xs font-semibold flex items-center gap-1.5 disabled:opacity-50 disabled:cursor-not-allowed ${
                    c.enabled
                      ? 'bg-amber-50 hover:bg-amber-100 text-amber-700'
                      : 'bg-emerald-50 hover:bg-emerald-100 text-emerald-700'
                  }`}
                >
                  {toggleBusy.has(c.id)
                    ? <Loader2 className="w-3 h-3 animate-spin" />
                    : (c.enabled ? <PowerOff className="w-3 h-3" /> : <Power className="w-3 h-3" />)}
                  {c.enabled ? 'Disable' : 'Enable'}
                </button>
                <button onClick={() => remove(c)} data-testid={`company-remove-${c.id}`}
                  className="px-3 py-1.5 bg-red-50 hover:bg-red-100 text-red-600 rounded-lg text-xs font-semibold flex items-center gap-1.5 ml-auto">
                  <Trash2 className="w-3 h-3" /> Delete
                </button>
              </div>
            </div>
          );
        })}
      </div>

      {/* Edit page — full-width replacement of the company list,
          but still rendered INSIDE the Settings content area so the
          left-side category sidebar (My account, Tuning, Payments,
          Communications, …) remains visible & navigable. */}
      {editing && (
        <div data-testid="company-edit-page" className="space-y-4">
            {/* Sticky header bar with back/cancel/save */}
            <div className="sticky top-0 z-20 bg-gradient-to-r from-slate-800 to-slate-900 text-white px-5 py-3 rounded-xl flex items-center justify-between shadow-md">
              <div className="flex items-center gap-3 min-w-0">
                <button
                  onClick={cancelEdit}
                  data-testid="company-page-back"
                  className="p-1.5 hover:bg-white/10 rounded inline-flex items-center gap-1.5 text-xs font-semibold flex-shrink-0"
                >
                  <X className="w-4 h-4" /> Back to companies
                </button>
                <span className="text-white/30">|</span>
                <h3 className="text-sm font-bold truncate">
                  {editing === 'new' ? 'Add company' : `Edit — ${draft.name || 'Untitled'}`}
                </h3>
              </div>
              <div className="flex items-center gap-2 flex-shrink-0">
                <button onClick={cancelEdit} data-testid="company-cancel"
                  className="px-3 py-1.5 bg-white/10 hover:bg-white/20 text-white rounded text-xs font-semibold">Cancel</button>
                <button onClick={save} disabled={saving || !draft.name?.trim()} data-testid="company-save"
                  className="px-4 py-1.5 bg-emerald-500 hover:bg-emerald-600 text-white rounded text-xs font-bold disabled:opacity-50 flex items-center gap-1.5">
                  {saving ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Save className="w-3.5 h-3.5" />} Save changes
                </button>
              </div>
            </div>

            {/* Quick switch between companies — keeps the operator
                inside the editor when jumping between cards. */}
            {editing !== 'new' && companies.length > 1 && (
              <div className="bg-white border border-slate-200 rounded-xl p-3 flex items-center gap-2 flex-wrap" data-testid="company-quick-switch">
                <span className="text-[11px] font-bold uppercase tracking-wide text-slate-500 mr-1">Switch to:</span>
                {companies.filter(c => c.id !== editing).map(c => (
                  <button
                    key={c.id}
                    onClick={() => beginEdit(c)}
                    data-testid={`company-switch-${c.id}`}
                    className="px-2.5 py-1 rounded text-[11px] font-semibold bg-slate-100 hover:bg-cyan-100 hover:text-cyan-800 text-slate-700"
                  >
                    {c.name}{c.is_default ? ' ★' : ''}
                  </button>
                ))}
              </div>
            )}

            {err && (
              <div className="p-3 bg-red-50 border border-red-200 rounded-lg text-sm text-red-700">
                {err}
              </div>
            )}

            {/* Two-column layout — kicks in earlier (lg ≥1024px)
                so the invoice editor + previews get max horizontal
                room once the Settings sidebar is collapsed-or-narrow.
                LEFT  (340px) — compact form
                RIGHT (rest)  — invoice template editor + previews */}
            <div className="grid grid-cols-1 lg:grid-cols-[340px_1fr] gap-4">
              {/* LEFT: Form fields */}
              <div className="bg-white rounded-xl border border-slate-200 p-5 space-y-3 h-fit">
                <h4 className="text-xs font-bold uppercase tracking-wide text-slate-500 mb-2">Billing details</h4>
                {/* Core billing fields */}
                {[
                  ['name', 'Company name *', 'text', 'ENCORE-PERFORMANCE LTD'],
                  ['company_number', 'Company number', 'text', '12569942'],
                  ['vat', 'VAT number', 'text', 'GB123456789'],
                  ['address', 'Address', 'text', '85 Great Portland Street'],
                  ['city', 'City', 'text', 'London'],
                  ['country', 'Country / postcode', 'text', 'United Kingdom, W1W 7LT'],
                  ['phone', 'Phone', 'text', '+44 ...'],
                  ['email', 'Email', 'email', 'invoices@example.com'],
                  ['website', 'Website', 'url', 'https://example.com'],
                  ['invoice_prefix', 'Invoice prefix', 'text', 'INV'],
                ].map(([key, label, type, ph]) => (
                  <div key={key}>
                    <label className="block text-xs font-bold text-slate-700 mb-1">{label}</label>
                    <input type={type} value={draft[key] ?? ''}
                      onChange={(e) => setDraft({ ...draft, [key]: e.target.value })}
                      placeholder={ph} data-testid={`company-${key}`}
                      className="w-full px-3 py-2 border border-slate-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-cyan-500" />
                  </div>
                ))}
                {/* Inline-stored logo */}
                <div>
                  <label className="block text-xs font-bold text-slate-700 mb-1">Company logo (used on invoices)</label>
                  <LogoUpload
                    value={draft.logo_data_url || draft.logo_url || ''}
                    onChange={(dataUrl) => setDraft({ ...draft, logo_data_url: dataUrl, logo_url: '' })}
                    testid="company-logo-upload"
                  />
                </div>
                <div>
                  <label className="block text-xs font-bold text-slate-700 mb-1">Bank details / payment instructions</label>
                  <textarea rows={4} value={draft.bank_details || ''}
                    onChange={(e) => setDraft({ ...draft, bank_details: e.target.value })}
                    placeholder={'Bank: ...\nIBAN: ...\nSWIFT/BIC: ...'}
                    data-testid="company-bank_details"
                    className="w-full px-3 py-2 border border-slate-300 rounded-lg text-sm font-mono" />
                </div>
                <label className="flex items-center gap-2 mt-2">
                  <input type="checkbox" checked={!!draft.is_default}
                    onChange={(e) => setDraft({ ...draft, is_default: e.target.checked })}
                    data-testid="company-is_default" className="w-4 h-4 text-cyan-600 rounded" />
                  <span className="text-sm font-semibold text-slate-800">Use this as the default company on invoices</span>
                </label>

                {/* Payment routing */}
                <div className="mt-4 p-4 bg-gradient-to-br from-slate-50 to-cyan-50 border-2 border-cyan-200 rounded-xl" data-testid="company-routing-block">
                  <h4 className="text-sm font-bold text-slate-800 mb-3 flex items-center gap-2">
                    <Building2 className="w-4 h-4 text-cyan-700" /> PayPal payment routing
                  </h4>
                  <div className="grid grid-cols-1 gap-3">
                    <div>
                      <label className="block text-xs font-bold text-slate-700 mb-1">Role (country cluster)</label>
                      <select value={draft.role || ''} onChange={(e) => setDraft({ ...draft, role: e.target.value })}
                        data-testid="company-role"
                        className="w-full px-3 py-2 border border-slate-300 rounded-lg text-sm bg-white">
                        <option value="">— No role (not a PayPal route) —</option>
                        <option value="USA_COMPANY">USA Company</option>
                        <option value="UK_COMPANY">UK Company</option>
                        <option value="EU_COMPANY">EU Company</option>
                      </select>
                    </div>
                    <label className="flex items-center gap-2 px-3 py-2 bg-white border border-slate-300 rounded-lg cursor-pointer">
                      <input type="checkbox" checked={draft.enabled !== false}
                        onChange={(e) => setDraft({ ...draft, enabled: e.target.checked })}
                        data-testid="company-enabled" className="w-4 h-4" />
                      <span className="text-sm font-semibold text-slate-800">
                        {draft.enabled !== false ? 'Enabled (accepts payments)' : 'Disabled (skip in routing)'}
                      </span>
                    </label>
                  </div>
                  {draft.role && (
                    <div className="mt-3 pt-3 border-t border-cyan-200">
                      <label className="flex items-center gap-2 mb-2">
                        <input type="checkbox" checked={!!draft.apply_tax}
                          onChange={(e) => setDraft({ ...draft, apply_tax: e.target.checked })}
                          data-testid="company-apply_tax" className="w-4 h-4" />
                        <span className="text-sm font-semibold">Apply tax on payments to this company</span>
                      </label>
                      {draft.apply_tax && (
                        <div className="ml-6">
                          <label className="block text-[11px] font-bold text-slate-600 mb-1">Tax rate (%)</label>
                          <input type="number" step="0.5" min="0" max="100" value={draft.tax_rate || 0}
                            onChange={(e) => setDraft({ ...draft, tax_rate: Number(e.target.value) || 0 })}
                            data-testid="company-tax_rate"
                            className="w-32 px-3 py-2 border border-amber-300 rounded-lg text-sm bg-amber-50 font-bold" />
                        </div>
                      )}
                    </div>
                  )}
                </div>

                {/* PayPal dual-mode credentials */}
                {draft.role && (
                  <div className="mt-4 p-4 bg-gradient-to-br from-blue-50 to-indigo-50 border-2 border-blue-200 rounded-xl" data-testid="company-paypal-block">
                    <div className="flex items-start justify-between gap-3 mb-3">
                      <div>
                        <h4 className="text-sm font-bold text-slate-800 flex items-center gap-2">
                          <KeyRound className="w-4 h-4 text-blue-700" /> PayPal merchant credentials
                        </h4>
                        <p className="text-[11px] text-slate-500 mt-0.5">
                          Separate creds for Sandbox & Live. Pick the active one below.
                        </p>
                      </div>
                      <label className="flex items-center gap-2 px-3 py-2 bg-white border border-blue-300 rounded-lg cursor-pointer">
                        <input type="checkbox" checked={draft.paypal_enabled !== false}
                          onChange={(e) => setDraft({ ...draft, paypal_enabled: e.target.checked })}
                          data-testid="company-paypal_enabled" className="w-4 h-4" />
                        <span className="text-xs font-semibold text-slate-800">
                          {draft.paypal_enabled !== false ? 'PayPal ON' : 'PayPal OFF'}
                        </span>
                      </label>
                    </div>

                    {/* Active mode radio */}
                    <div className="mb-3 p-3 bg-white rounded-lg border border-blue-200">
                      <label className="block text-[11px] font-bold text-slate-600 uppercase tracking-wide mb-2 flex items-center gap-1.5">
                        <Radio className="w-3 h-3" /> Active mode (which one receives real traffic)
                      </label>
                      <div className="grid grid-cols-1 gap-2">
                        {['sandbox', 'live'].map(m => (
                          <label key={m}
                            className={`flex items-center gap-2 px-3 py-2 border-2 rounded-lg cursor-pointer transition-colors ${draft.paypal_mode === m ? (m === 'live' ? 'border-green-500 bg-green-50' : 'border-blue-500 bg-blue-50') : 'border-slate-200 bg-white hover:border-slate-300'}`}>
                            <input type="radio" name="paypal_mode_radio" checked={draft.paypal_mode === m}
                              onChange={() => setDraft({ ...draft, paypal_mode: m })}
                              data-testid={`company-paypal-active-${m}`}
                              className="w-4 h-4" />
                            <div className="text-sm">
                              <div className="font-bold text-slate-800">{m === 'sandbox' ? '🧪 Sandbox' : '🟢 Live (production)'}</div>
                              <div className="text-[10px] text-slate-500">{m === 'sandbox' ? 'test payments, fake money' : 'real payments, real money'}</div>
                            </div>
                          </label>
                        ))}
                      </div>
                    </div>

                    {/* Tab switcher */}
                    <div className="flex gap-1 mb-2 border-b border-blue-200">
                      {['sandbox', 'live'].map(m => (
                        <button key={m} type="button" onClick={() => setActiveTab(m)}
                          data-testid={`company-paypal-tab-${m}`}
                          className={`px-4 py-2 text-xs font-bold transition-colors flex items-center gap-1.5 ${activeTab === m ? (m === 'live' ? 'border-b-2 border-green-500 text-green-700' : 'border-b-2 border-blue-500 text-blue-700') : 'text-slate-500 hover:text-slate-700'}`}>
                          {m === 'sandbox' ? '🧪' : '🟢'} {m.toUpperCase()}
                          {draft.paypal_mode === m && <span className="ml-1 text-[9px] bg-amber-100 text-amber-800 px-1 rounded">ACTIVE</span>}
                        </button>
                      ))}
                    </div>

                    {/* Active tab content */}
                    {['sandbox', 'live'].map(m => activeTab === m && (
                      <EnvCredsBlock key={m}
                        mode={m}
                        env={draft[`paypal_${m}`] || emptyEnv()}
                        showSecret={showSecret[m]}
                        onToggleSecret={() => setShowSecret(s => ({ ...s, [m]: !s[m] }))}
                        onChange={(k, v) => setEnvField(m, k, v)}
                        onTest={() => testPaypal(m)}
                        testing={testing === m}
                        testResult={testResult[m]}
                        canTest={editing !== 'new' && !!draft[`paypal_${m}`]?.client_id}
                      />
                    ))}
                  </div>
                )}
              </div>

              {/* RIGHT: Invoice template editor (full width / big preview) */}
              <div className="space-y-4">
                {/* Logo placement editor — needs an existing record */}
                {draft.id && (
                  <div className="bg-white rounded-xl border border-slate-200 p-5">
                    <LogoPlacementEditor
                      companyId={draft.id}
                      token={token}
                      value={draft.invoice_settings}
                      onChange={(next) => setDraft({ ...draft, invoice_settings: next })}
                    />
                  </div>
                )}

                {/* Full HTML invoice template — only after first save */}
                {draft.id ? (
                  <div className="bg-white rounded-xl border border-slate-200 p-5">
                    <InvoiceHtmlEditor
                      token={token}
                      title={`Invoice template — ${draft.name || 'Company'}`}
                      subtitle="Full WYSIWYG editor. English only. Use the placeholders sidebar to insert dynamic fields. Click Preview PDF to see the rendered invoice."
                      loadUrl={`${API_URL}/api/companies/${draft.id}/invoice-template-html`}
                      saveUrl={`${API_URL}/api/companies/${draft.id}/invoice-template-html`}
                      previewUrl={`${API_URL}/api/companies/${draft.id}/invoice-html-preview`}
                      showEngineToggle
                    />
                  </div>
                ) : (
                  <div className="bg-amber-50 border border-amber-300 rounded-xl p-5 flex items-start gap-3">
                    <Info className="w-5 h-5 text-amber-700 mt-0.5 flex-shrink-0" />
                    <div>
                      <p className="text-sm font-bold text-amber-900">Save the company first</p>
                      <p className="text-xs text-amber-800 mt-1">
                        The invoice template editor and logo placement controls unlock as soon as
                        you save this new company at least once. Fill in the billing details on
                        the left and hit <strong>Save changes</strong>.
                      </p>
                    </div>
                  </div>
                )}
              </div>
            </div>
        </div>
      )}
    </div>
  );
}


function EnvCredsBlock({ mode, env, showSecret, onToggleSecret, onChange, onTest, testing, testResult, canTest }) {
  const color = mode === 'live' ? 'green' : 'blue';
  return (
    <div className="p-3 bg-white rounded-lg border border-slate-200 space-y-3" data-testid={`company-paypal-${mode}-block`}>
      <div className="flex items-center justify-between">
        <p className="text-[11px] text-slate-500">
          From <a href={`https://developer.paypal.com/dashboard/applications/${mode}`} target="_blank" rel="noreferrer"
             className={`text-${color}-600 hover:underline font-semibold`}>PayPal Developer Dashboard → {mode}</a> → your Merchant app.
        </p>
        <label className="flex items-center gap-1.5 text-xs font-semibold cursor-pointer">
          <input type="checkbox" checked={!!env.enabled}
            onChange={(e) => onChange('enabled', e.target.checked)}
            data-testid={`company-paypal-${mode}-enabled`} className="w-3.5 h-3.5" />
          <span className="text-slate-700">{env.enabled ? 'Enabled' : 'Disabled'}</span>
        </label>
      </div>
      <div>
        <label className="block text-xs font-bold text-slate-700 mb-1">Client ID</label>
        <input type="text" value={env.client_id || ''}
          onChange={(e) => onChange('client_id', e.target.value.trim())}
          placeholder={mode === 'live' ? 'A_xxxxxxxxxxxxxxxxxxxxxxxxxxxx (live)' : 'A_xxxxxxxxxxxxxxxxxxxxxxxxxxxx (sandbox)'}
          data-testid={`company-paypal-${mode}-client_id`}
          className="w-full px-3 py-2 border border-slate-300 rounded-lg text-sm font-mono" />
      </div>
      <div>
        <label className="block text-xs font-bold text-slate-700 mb-1 flex items-center justify-between">
          <span>Secret key
            {env.secret_set && <span className="text-emerald-600 ml-1">✓ Saved (ends with <code className="bg-emerald-50 px-1 rounded">…{env.secret_last4}</code>)</span>}
          </span>
          <button type="button" onClick={onToggleSecret} className="text-slate-500 hover:text-slate-800 inline-flex items-center gap-1 text-[11px]" data-testid={`company-paypal-${mode}-secret-toggle`}>
            {showSecret ? (<><EyeOff className="w-3 h-3" /> Hide</>) : (<><Eye className="w-3 h-3" /> Show</>)}
          </button>
        </label>
        <input type={showSecret ? 'text' : 'password'} value={env.secret || ''}
          onChange={(e) => onChange('secret', e.target.value)}
          placeholder={env.secret_set ? 'Leave empty to keep saved secret' : 'E_xxxxxxxxxxxxxxxxxxxxxxxx'}
          data-testid={`company-paypal-${mode}-secret`}
          // Stop browser/password-manager autofill from quietly pasting
          // the user's site-login password into this field. The
          // randomised `name` defeats heuristic-based autofill in
          // Chrome/Edge; data-1p-ignore + data-lpignore stop 1Password
          // and LastPass respectively.
          name={`paypal_${mode}_secret_${Math.random().toString(36).slice(2, 10)}`}
          autoComplete="new-password"
          data-1p-ignore="true"
          data-lpignore="true"
          data-form-type="other"
          spellCheck={false}
          className="w-full px-3 py-2 border border-slate-300 rounded-lg text-sm font-mono" />
      </div>
      <div>
        <label className="block text-xs font-bold text-slate-700 mb-1">Webhook ID <span className="text-slate-400 font-normal">(for signature verification)</span></label>
        <input type="text" value={env.webhook_id || ''}
          onChange={(e) => onChange('webhook_id', e.target.value.trim())}
          placeholder="WH-XXXXXXXX-XXXXXXXX"
          data-testid={`company-paypal-${mode}-webhook_id`}
          className="w-full px-3 py-2 border border-slate-300 rounded-lg text-sm font-mono" />
      </div>
      <PaypalWebhookHelper mode={mode} />
      <div className="flex items-center gap-3 pt-2 border-t border-slate-100">
        <button type="button" onClick={onTest} disabled={testing || !canTest}
          data-testid={`company-paypal-${mode}-test`}
          className={`px-4 py-2 bg-white hover:bg-${color}-50 border-2 border-${color}-300 rounded-lg text-sm font-bold text-${color}-700 disabled:opacity-50 flex items-center gap-2`}>
          {testing ? <Loader2 className="w-4 h-4 animate-spin" /> : <Zap className="w-4 h-4" />} Test {mode.toUpperCase()}
        </button>
        {testResult && (
          <div data-testid={`company-paypal-${mode}-test-result`}
            className={`flex-1 text-sm font-semibold flex items-center gap-2 ${testResult.success ? 'text-emerald-700' : 'text-red-700'}`}>
            {testResult.success ? <ShieldCheck className="w-4 h-4" /> : <ShieldAlert className="w-4 h-4" />}
            <span className="line-clamp-2">{testResult.message}</span>
          </div>
        )}
        {!canTest && <span className="text-xs text-slate-500 italic">Save first, then test</span>}
      </div>
    </div>
  );
}


/* PayPal Developer Dashboard requires that the merchant pre-registers a
 * webhook URL on their app and selects the event types they want to
 * receive. This helper surfaces both pieces inside the company editor
 * so the admin doesn't have to dig through documentation or guess what
 * URL to paste into the PayPal portal. Copy-to-clipboard buttons turn
 * a 5-minute cross-reference task into two clicks per company.
 *
 * Display-only — no credentials are exposed here. The events list is
 * derived from the actual backend webhook handler in
 * `backend/routes/paypal_webhooks.py` so the two stay in sync.
 */
const WEBHOOK_EVENTS = [
  'CHECKOUT.ORDER.APPROVED',
  'CHECKOUT.ORDER.COMPLETED',
  'PAYMENT.CAPTURE.COMPLETED',
  'PAYMENT.CAPTURE.DENIED',
  'PAYMENT.CAPTURE.PENDING',
  'PAYMENT.CAPTURE.REFUNDED',
  'PAYMENT.CAPTURE.REVERSED',
  'CUSTOMER.DISPUTE.CREATED',
  'CUSTOMER.DISPUTE.RESOLVED',
];

function PaypalWebhookHelper({ mode }) {
  const [copied, setCopied] = useState(null); // 'url' | 'events' | null
  const [open, setOpen]     = useState(false);
  const webhookUrl = `${API_URL}/api/paypal/webhook`;
  const eventsStr  = WEBHOOK_EVENTS.join(', ');
  const copy = async (key, value) => {
    try {
      await navigator.clipboard.writeText(value);
      setCopied(key);
      setTimeout(() => setCopied(null), 1500);
    } catch (_) { /* clipboard unavailable */ }
  };
  return (
    <div className="rounded-lg border border-amber-200 bg-amber-50/70" data-testid={`paypal-webhook-helper-${mode}`}>
      <button
        type="button"
        onClick={() => setOpen(o => !o)}
        className="w-full flex items-center justify-between px-3 py-2 text-left"
        data-testid={`paypal-webhook-helper-${mode}-toggle`}
      >
        <span className="text-[12px] font-bold text-amber-900 flex items-center gap-1.5">
          <Info className="w-3.5 h-3.5" /> How to register this webhook at PayPal
        </span>
        <span className="text-[10px] font-semibold text-amber-700 uppercase tracking-wider">
          {open ? 'hide' : 'show'}
        </span>
      </button>
      {open && (
        <div className="px-3 pb-3 space-y-3 text-[12px] text-slate-700 border-t border-amber-200">
          <ol className="list-decimal ml-4 space-y-1">
            <li>
              Open{' '}
              <a
                href={`https://developer.paypal.com/dashboard/applications/${mode}`}
                target="_blank" rel="noreferrer"
                className="text-blue-700 font-semibold hover:underline inline-flex items-center gap-0.5"
              >
                PayPal Developer Dashboard → {mode}
                <ExternalLink className="w-3 h-3" />
              </a>{' '}
              and pick your Merchant app.
            </li>
            <li>
              Section <b>Webhooks</b> → <b>Add Webhook</b> → paste the URL below.
            </li>
            <li>Select the event types listed below → Save.</li>
            <li>Copy the resulting <b>Webhook ID</b> back into the field above.</li>
          </ol>

          <div>
            <div className="text-[10px] uppercase tracking-wider font-bold text-amber-900 mb-1">
              Webhook URL (paste into PayPal)
            </div>
            <div className="flex items-center gap-2">
              <code
                className="flex-1 px-2 py-1.5 bg-white border border-amber-200 rounded text-[11px] font-mono break-all"
                data-testid={`paypal-webhook-url-${mode}`}
              >
                {webhookUrl}
              </code>
              <button
                type="button"
                onClick={() => copy('url', webhookUrl)}
                className="px-2 py-1.5 bg-white border border-amber-300 rounded text-[11px] font-bold text-amber-800 hover:bg-amber-100 flex items-center gap-1 flex-shrink-0"
                data-testid={`paypal-webhook-url-copy-${mode}`}
              >
                {copied === 'url' ? <Check className="w-3 h-3" /> : <Copy className="w-3 h-3" />}
                {copied === 'url' ? 'Copied' : 'Copy'}
              </button>
            </div>
            <p className="text-[10px] text-slate-500 mt-1">
              Same URL for every company / mode — PayPal sends the webhook
              ID inside each event, the backend uses that to look up which
              company &amp; mode the event belongs to.
            </p>
          </div>

          <div>
            <div className="flex items-center justify-between mb-1">
              <span className="text-[10px] uppercase tracking-wider font-bold text-amber-900">
                Event types to subscribe ({WEBHOOK_EVENTS.length})
              </span>
              <button
                type="button"
                onClick={() => copy('events', eventsStr)}
                className="px-2 py-0.5 bg-white border border-amber-300 rounded text-[10px] font-bold text-amber-800 hover:bg-amber-100 inline-flex items-center gap-1"
                data-testid={`paypal-webhook-events-copy-${mode}`}
              >
                {copied === 'events' ? <Check className="w-3 h-3" /> : <Copy className="w-3 h-3" />}
                {copied === 'events' ? 'Copied' : 'Copy list'}
              </button>
            </div>
            <ul
              className="grid grid-cols-1 sm:grid-cols-2 gap-x-3 gap-y-0.5"
              data-testid={`paypal-webhook-events-${mode}`}
            >
              {WEBHOOK_EVENTS.map(e => (
                <li key={e} className="text-[11px] font-mono text-slate-700 flex items-center gap-1.5">
                  <span className="w-1 h-1 rounded-full bg-amber-500" /> {e}
                </li>
              ))}
            </ul>
          </div>
        </div>
      )}
    </div>
  );
}
